const STEP_NAME_TO_ROUTE = {
    StepOne: "step1",
    StepTwo: "step2",
    StepThree: "step3",
};

function decodeRoutePart(value) {
    try {
        return decodeURIComponent(value);
    } catch (error) {
        // sentry-ignore: undecodable route part is used as-is
        void error;
        return value;
    }
}

function normalizeRouteVariant(value) {
    return decodeRoutePart(String(value || ""))
        .normalize("NFKC")
        .toLowerCase();
}

export function getNumericVariantId(routeVariant) {
    return String(routeVariant || "").match(/^(\d+)(?:_|$)/)?.[1] || "";
}

export function getStepRoute(stepName) {
    return STEP_NAME_TO_ROUTE[stepName] || "";
}

export function extractBuilderRoute(value) {
    const routeValue = String(value || "");
    const hashMatch = routeValue.match(
        /#\/([^/?#]+)\/(step[123])(?:[/?#]|$)/
    );
    const pathMatch = routeValue.match(
        /(?:^|\/)([^/?#]+)\/(step[123])(?:[/?#]|$)/
    );
    const match = hashMatch || pathMatch;

    if (!match) return null;

    return {
        routeVariant: decodeRoutePart(match[1]),
        step: match[2],
    };
}

function getEntryRouteValues(entry) {
    const queryValues = Array.isArray(entry?.query)
        ? entry.query
            .filter((query) =>
                query?.property === "urlPath" &&
                query?.operator === "is" &&
                typeof query?.value === "string"
            )
            .map((query) => query.value)
        : [];

    return [entry?.data?.url, ...queryValues].filter(Boolean);
}

function getCanonicalRoutes(entries, numericVariantId, step) {
    const routesByPath = new Map();

    for (const entry of entries || []) {
        if (entry?.published && entry.published !== "published") continue;

        for (const value of getEntryRouteValues(entry)) {
            const route = extractBuilderRoute(value);
            if (!route || route.step !== step) continue;
            if (getNumericVariantId(route.routeVariant) !== numericVariantId) {
                continue;
            }

            const path = `/${route.routeVariant}/${route.step}`;
            routesByPath.set(path, { ...route, path });
        }
    }

    return [...routesByPath.values()];
}

export function resolveCanonicalBuilderRoute({
    routeVariant,
    stepName,
    entries,
}) {
    const numericVariantId = getNumericVariantId(routeVariant);
    const step = getStepRoute(stepName);

    if (!numericVariantId || !step) {
        return { status: "invalid_route", candidateRouteCount: 0 };
    }

    const routes = getCanonicalRoutes(entries, numericVariantId, step);
    if (routes.length === 0) {
        return { status: "no_candidate", candidateRouteCount: 0 };
    }

    const exactMatches = routes.filter(
        (route) => route.routeVariant === routeVariant
    );
    if (exactMatches.length === 1) {
        return {
            status: "exact_match",
            candidateRouteCount: routes.length,
        };
    }

    const normalizedVariant = normalizeRouteVariant(routeVariant);
    const normalizedMatches = routes.filter(
        (route) =>
            normalizeRouteVariant(route.routeVariant) === normalizedVariant
    );

    if (normalizedMatches.length === 1) {
        return {
            status: "canonical_match",
            candidateRouteCount: routes.length,
            routeVariant: normalizedMatches[0].routeVariant,
            path: normalizedMatches[0].path,
        };
    }

    if (normalizedMatches.length > 1) {
        return {
            status: "ambiguous",
            candidateRouteCount: routes.length,
        };
    }

    if (routes.length === 1) {
        return {
            status: "unique_candidate",
            candidateRouteCount: 1,
            routeVariant: routes[0].routeVariant,
            path: routes[0].path,
        };
    }

    return {
        status: "ambiguous",
        candidateRouteCount: routes.length,
    };
}
